import { TenantFeatureToggle, CustomerAdditionalInfoDisplay } from '@xeetr/types/enums'
import { Settings } from '@xeetr/types/models'
import { format } from 'date-fns'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import useFeatureToggle from '../../../../hooks/common/useFeatureToggle'
import LabeledInputNumber from '../../../inputs/LabeledInputNumber'
import LabeledInputSwitch from '../../../inputs/LabeledInputSwitch'
import TimePicker from '../../common/TimePicker'

import { cn } from '@/lib/utils'
import { showToast } from '@/utils/toastMessage'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const FoodTenantReservation = () => {
  const { control, setValue } = useFormContext<Settings>()
  const tableManagementToggle = useFeatureToggle(TenantFeatureToggle.TABLEMANAGEMENT)
  const useLengthOfStay = useWatch({ control, name: 'tenant.settings.useLengthOfStay' })

  const defaultLengthOfStay = useWatch({ control, name: 'tenant.settings.defaultLengthOfStay' })

  const handleAddLengthOfStay = (
    date: Date,
    value: string[],
    onChange: (value: string[]) => void
  ) => {
    const time = format(date, 'HH:mm')
    if (value.includes(time)) {
      showToast({ summary: 'Warnung', severity: 'warn', detail: 'Zeit wurde bereits hinterlegt!' })
      return
    }
    const sortedLengthOfStays = [time, ...value].sort()
    onChange(sortedLengthOfStays)
    if (sortedLengthOfStays.length === 1) {
      handleDefaultLengthOfStayChange(sortedLengthOfStays[0])
    }
  }

  const handleRemoveLengthOfStay = (
    time: string,
    values: string[],
    onChange: (value: string[]) => void
  ) => {
    const filteredLengthOfStays = values.filter((v) => v !== time)
    if (time === defaultLengthOfStay) {
      handleDefaultLengthOfStayChange(filteredLengthOfStays[0] ?? '')
    }
    onChange(filteredLengthOfStays)
  }

  const handleDefaultLengthOfStayChange = (value: string) => {
    setValue('tenant.settings.defaultLengthOfStay', value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  return (
    <>
      <Controller
        name="tenant.settings.onlineBookingPersonAmount"
        control={control}
        render={({ field }) => (
          <LabeledInputNumber
            label="Maximale Gruppengröße bei online Reservierungen (0 bedeutet unbegrenzt)"
            labelClassName="md:col-span-6"
            inputId={field.name}
            inputRef={field.ref}
            value={field.value}
            min={0}
            onChange={field.onChange}
          />
        )}
      />
      <Controller
        name="tenant.settings.includeCancelLinkInCalendarAttachment"
        control={control}
        render={({ field }) => (
          <LabeledInputSwitch
            className="col-span-1 py-3 md:col-span-6"
            checked={field.value ?? false}
            name={field.name}
            ref={field.ref}
            label="Stornierungslink im Kalender-Anhang anzeigen"
            onValueChange={field.onChange}
          />
        )}
      />
      {tableManagementToggle && (
        <>
          <Controller
            name="tenant.settings.useLengthOfStay"
            control={control}
            render={({ field }) => (
              <LabeledInputSwitch
                className="col-span-1 py-3 md:col-span-6"
                checked={field.value ?? false}
                name={field.name}
                ref={field.ref}
                label="Verweildauer für Kunden nutzen"
                onValueChange={(event) => {
                  field.onChange(event)
                  if (event.valueOf() === false) {
                    setValue('tenant.settings.useAutoAccept', false, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    })
                  }
                }}
              />
            )}
          />
          <div
            className={cn(
              'mb-2 grid gap-5 sm:ml-14 md:col-span-6 md:grid-cols-[1fr,3fr] md:items-center',
              {
                hidden: !useLengthOfStay,
              }
            )}
          >
            <Controller
              name="tenant.settings.lengthOfStay"
              render={({ field }) => (
                <TimePicker
                  values={field.value}
                  defaultValue={defaultLengthOfStay}
                  onChange={(date) => handleAddLengthOfStay(date, field.value, field.onChange)}
                  onRemove={(time) => handleRemoveLengthOfStay(time, field.value, field.onChange)}
                  setDefaultValue={handleDefaultLengthOfStayChange}
                />
              )}
            />
          </div>
        </>
      )}
      {useLengthOfStay && (
        <Controller
          name="tenant.settings.useAutoAccept"
          control={control}
          render={({ field }) => (
            <div className="col-span-1 pt-2 md:col-span-6">
              <LabeledInputSwitch
                checked={field.value ?? false}
                name={field.name}
                ref={field.ref}
                label="Reservierung automatisch akzeptieren"
                onValueChange={field.onChange}
              />
              <p className="mt-1 text-xs text-gray-500">
                Wenn aktiviert, werden Reservierungen automatisch angenommen und einem passenden
                Tisch zugewiesen, sofern einer verfügbar ist.
              </p>
            </div>
          )}
        />
      )}
      <section className="mt-8">
        <h3 className="mb-2 text-lg font-semibold">Kundeninformationen</h3>
        <p className="text-muted-foreground mb-4 text-sm">
          Hier kannst du steuern, ob und wie die Zusatzinformationen der Kunden bei Reservierungen
          angezeigt werden.
        </p>
        <Controller
          name="tenant.settings.customerAdditionalInfoDisplay"
          control={control}
          render={({ field }) => (
            <div className="col-span-1 py-3 md:col-span-6">
              <label htmlFor={field.name} className="mb-1 block text-sm font-medium">
                Anzeige der Zusatzinformationen des Kunden
              </label>
              <Select
                value={field.value || CustomerAdditionalInfoDisplay.DISPLAY}
                onValueChange={field.onChange}
              >
                <SelectTrigger id={field.name}>
                  <SelectValue placeholder="Bitte auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CustomerAdditionalInfoDisplay.DISPLAY}>
                    Immer anzeigen
                  </SelectItem>
                  <SelectItem value={CustomerAdditionalInfoDisplay.HIDDEN}>
                    In &quot;Mehr anzeigen&quot; Bereich
                  </SelectItem>
                  <SelectItem value={CustomerAdditionalInfoDisplay.COMPLETELY_HIDDEN}>
                    Nie anzeigen
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-gray-500">
                Steuert, ob und wie die Zusatzinformationen des Kunden bei Reservierungen angezeigt
                werden.
              </p>
            </div>
          )}
        />
      </section>
    </>
  )
}

export default FoodTenantReservation
